import * as React from 'react';

import { RecommendationDesktop, RecommendationDesktopSkeleton } from '../../components/RecommendationDesktop';
import { RecommendationsListDesktop } from '../../components/RecommendationsListDesktop';
import { RecommendationsListDesktopWithNewbuildings } from '../../components/RecommendationsListDesktopWithNewbuildings';
import { ELoadingStatus } from '../../types/loadingStatus';
import { FavoriteButtonContainer } from '../FavoriteButtonContainer';
import { LabelsContainer } from '../LabelsContainer';
import { NewbuildingRecommendations } from '../NewbuildingRecommendations/NewbuildingRecommendations';
import { DesktopRedesignRecommendationsExperiment } from './DesktopRedesignRecommendationsExperiment';
import { RedesignedRecommendationsListDesktopContainer } from './RedesignedRecommendationsListDesktopContainer';
import type { useRecommendations } from './useRecommendations';

type TTrackRecommendationShow = (offerId: number | null, position: number, contextToken?: string) => void;

type TRecommendationsListDesktopContainerProps = {
  recommendationsControls: ReturnType<typeof useRecommendations>;
  onTrack: TTrackRecommendationShow;
  showNewbuildingRecommendations: boolean;
};

export const RecommendationsListDesktopContainer: React.FC<TRecommendationsListDesktopContainerProps> = ({
  recommendationsControls,
  onTrack,
  showNewbuildingRecommendations,
}) => (
  <DesktopRedesignRecommendationsExperiment
    legacy={
      <RecommendationsListDesktopLegacyContainer
        recommendationsControls={recommendationsControls}
        onTrack={onTrack}
        showNewbuildingRecommendations={showNewbuildingRecommendations}
      />
    }
    redesigned={
      <RedesignedRecommendationsListDesktopContainer
        recommendationsControls={recommendationsControls}
        onTrack={onTrack}
        showNewbuildingRecommendations={showNewbuildingRecommendations}
      />
    }
  />
);

const RecommendationsListDesktopLegacyContainer: React.FC<TRecommendationsListDesktopContainerProps> = ({
  recommendationsControls,
  onTrack,
  showNewbuildingRecommendations,
}) => {
  const {
    recommendations,
    onRecommendationClick,
    onShow,
    onShowTrap,
    recommendationsStatus,
    regionId,
    onMoreClick,
    isOfferVisited,
    showFullList,
  } = recommendationsControls;

  const desktopSkeletonCount = showFullList ? 40 : 8;

  const recommendationsDesktopSkeleton = React.useMemo(
    () =>
      Array.from({ length: desktopSkeletonCount }, (_, index) => (
        <RecommendationDesktopSkeleton key={`recommendation-desktop-skeleton-${index}`} />
      )),
    [desktopSkeletonCount],
  );

  if (recommendationsStatus === ELoadingStatus.Loading) {
    return (
      <RecommendationsListDesktop showMoreLink={!showFullList}>
        {recommendationsDesktopSkeleton}
      </RecommendationsListDesktop>
    );
  }

  if (!recommendations.length) {
    return null;
  }

  const recommendationCards = recommendations.map((recommendation, index) => (
    <RecommendationDesktop
      {...recommendation}
      onTrack={onTrack}
      position={index + 1}
      key={recommendation.offerId}
      onClick={onRecommendationClick}
      onShowTrap={onShowTrap}
      regionId={regionId}
      favoriteButton={<FavoriteButtonContainer size="M" id={recommendation.offerId} />}
      labels={<LabelsContainer visited={isOfferVisited(recommendation.offerId)} />}
    />
  ));

  if (!showNewbuildingRecommendations) {
    return (
      <RecommendationsListDesktop onShow={onShow} onMoreClick={onMoreClick} showMoreLink={!showFullList}>
        {recommendationCards}
      </RecommendationsListDesktop>
    );
  }

  return (
    <RecommendationsListDesktopWithNewbuildings
      showMoreLink={!showFullList}
      secondRow={<NewbuildingRecommendations />}
      onShow={onShow}
      onMoreClick={onMoreClick}
    >
      {recommendationCards}
    </RecommendationsListDesktopWithNewbuildings>
  );
};
